/**
 * Analytics Service - GA4 + Backend dual tracking for conversion optimization
 * Pushes events to both Google Analytics 4 (gtag) and KadmiAi backend
 */

// Event types
export const ANALYTICS_EVENTS = {
  // Demo events
  DEMO_STARTED: 'demo_started',
  DEMO_COMPLETED: 'demo_completed',
  DEMO_CTA_CLICKED: 'demo_cta_clicked',
  
  // Auth events
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_COMPLETED: 'login_completed',
  
  // Payment events
  PAYMENT_CLICKED: 'payment_clicked',
  PAYMENT_STARTED: 'payment_started',
  PAYMENT_COMPLETED: 'payment_completed',
  
  // Feature events
  LETTER_GENERATED: 'letter_generated',
  PDF_EXPORTED: 'pdf_exported',
  DOCUMENT_SCANNED: 'document_scanned',
  
  // CTA click events
  CTA_ESSAYER_GRATUITEMENT: 'cta_essayer_gratuitement',
  CTA_GENERER_COURRIER: 'cta_generer_courrier',
  CTA_SE_CONNECTER: 'cta_se_connecter',
  
  // Engagement events
  CHAT_OPENED: 'chat_opened',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  SOCIAL_PROOF_SHOWN: 'social_proof_shown',
  
  // TTS events
  TTS_PLAY_CLICKED: 'tts_play_clicked',
  TTS_PAUSE_CLICKED: 'tts_pause_clicked',
  TTS_STOP_CLICKED: 'tts_stop_clicked',
  TTS_LANGUAGE_CHANGED: 'tts_language_changed',
  
  // Onboarding events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  
  // Page events
  PAGE_VIEW: 'page_view',
  SCROLL_DEPTH: 'scroll_depth',
  TIME_ON_PAGE: 'time_on_page'
};

// Analytics class
class Analytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = localStorage.getItem('userId') || null;
    this.events = [];
    this.startTime = Date.now();
  }

  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  // Push event to Google Analytics 4
  pushToGA4(eventName, params = {}) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, {
          ...params,
          session_id: this.sessionId,
          user_id: this.userId || undefined,
        });
      }
    } catch (e) {
      // Silent fail
    }
  }

  // Track an event (dual: GA4 + backend)
  track(eventName, properties = {}) {
    const event = {
      event: eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        url: window.location.href,
        referrer: document.referrer,
        screenWidth: window.innerWidth,
        language: navigator.language
      }
    };

    this.events.push(event);

    // Push to GA4
    this.pushToGA4(eventName, properties);

    // Send to backend
    this.sendToBackend(event);

    return event;
  }

  // Send event to backend (uses public endpoint if not authenticated)
  async sendToBackend(event) {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const endpoint = token ? '/api/analytics/track' : '/api/analytics/track-public';
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event_type: event.event,
          metadata: event.properties
        })
      });
    } catch (error) {
      // Silently fail
    }
  }

  setUserId(userId) {
    this.userId = userId;
    localStorage.setItem('userId', userId);
    // Set user ID in GA4
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('config', 'G-FTYJLJ0VLR', { user_id: userId });
      }
    } catch (e) { /* silent */ }
  }

  clearUserId() {
    this.userId = null;
    localStorage.removeItem('userId');
  }

  // Page view
  pageView(pageName, properties = {}) {
    return this.track(ANALYTICS_EVENTS.PAGE_VIEW, { page: pageName, ...properties });
  }

  // CTA tracking
  ctaEssayerGratuitement(location = 'unknown') {
    return this.track(ANALYTICS_EVENTS.CTA_ESSAYER_GRATUITEMENT, {
      event_category: 'cta',
      event_label: location,
      location
    });
  }

  ctaGenererCourrier(location = 'unknown') {
    return this.track(ANALYTICS_EVENTS.CTA_GENERER_COURRIER, {
      event_category: 'cta',
      event_label: location,
      location
    });
  }

  ctaSeConnecter(location = 'unknown') {
    return this.track(ANALYTICS_EVENTS.CTA_SE_CONNECTER, {
      event_category: 'cta',
      event_label: location,
      location
    });
  }

  // Demo tracking helpers
  demoStarted(inputText) {
    return this.track(ANALYTICS_EVENTS.DEMO_STARTED, {
      event_category: 'engagement',
      inputLength: inputText?.length || 0,
      inputPreview: inputText?.substring(0, 50) || ''
    });
  }

  demoCompleted(resultType, inputText) {
    return this.track(ANALYTICS_EVENTS.DEMO_COMPLETED, {
      event_category: 'engagement',
      resultType,
      inputLength: inputText?.length || 0
    });
  }

  demoCTAClicked(resultType) {
    return this.track(ANALYTICS_EVENTS.DEMO_CTA_CLICKED, {
      event_category: 'conversion',
      resultType
    });
  }

  // Auth tracking
  signupStarted(source = 'unknown') {
    return this.track(ANALYTICS_EVENTS.SIGNUP_STARTED, {
      event_category: 'auth',
      source
    });
  }

  signupCompleted(userId) {
    this.setUserId(userId);
    // GA4 conversion event
    this.pushToGA4('sign_up', { method: 'email' });
    return this.track(ANALYTICS_EVENTS.SIGNUP_COMPLETED, {
      event_category: 'auth',
      userId
    });
  }

  loginCompleted(userId) {
    this.setUserId(userId);
    this.pushToGA4('login', { method: 'email' });
    return this.track(ANALYTICS_EVENTS.LOGIN_COMPLETED, {
      event_category: 'auth',
      userId
    });
  }

  // Payment tracking (GA4 e-commerce)
  paymentClicked(plan, amount) {
    this.pushToGA4('begin_checkout', {
      currency: 'EUR',
      value: amount,
      items: [{ item_name: plan, price: amount }]
    });
    return this.track(ANALYTICS_EVENTS.PAYMENT_CLICKED, {
      event_category: 'payment',
      plan,
      amount
    });
  }

  paymentStarted(plan, amount) {
    return this.track(ANALYTICS_EVENTS.PAYMENT_STARTED, {
      event_category: 'payment',
      plan,
      amount
    });
  }

  paymentCompleted(plan, amount) {
    // GA4 purchase conversion event
    this.pushToGA4('purchase', {
      currency: 'EUR',
      value: amount,
      transaction_id: 'stripe_' + Date.now(),
      items: [{ item_name: plan, price: amount }]
    });
    return this.track(ANALYTICS_EVENTS.PAYMENT_COMPLETED, {
      event_category: 'payment',
      plan,
      amount
    });
  }

  // Feature tracking
  letterGenerated(templateId, templateName) {
    this.pushToGA4('generate_lead', {
      event_category: 'feature',
      value: 1,
      currency: 'EUR'
    });
    return this.track(ANALYTICS_EVENTS.LETTER_GENERATED, { templateId, templateName });
  }

  pdfExported(letterId) {
    return this.track(ANALYTICS_EVENTS.PDF_EXPORTED, {
      event_category: 'feature',
      letterId
    });
  }

  documentScanned() {
    return this.track(ANALYTICS_EVENTS.DOCUMENT_SCANNED, {
      event_category: 'feature'
    });
  }

  // TTS tracking
  ttsPlayClicked(language, textLength) {
    return this.track(ANALYTICS_EVENTS.TTS_PLAY_CLICKED, { language, textLength });
  }

  ttsPauseClicked(language) {
    return this.track(ANALYTICS_EVENTS.TTS_PAUSE_CLICKED, { language });
  }

  ttsStopClicked(language) {
    return this.track(ANALYTICS_EVENTS.TTS_STOP_CLICKED, { language });
  }

  ttsLanguageChanged(fromLang, toLang) {
    return this.track(ANALYTICS_EVENTS.TTS_LANGUAGE_CHANGED, { fromLang, toLang });
  }

  // Onboarding tracking
  onboardingStarted() {
    return this.track(ANALYTICS_EVENTS.ONBOARDING_STARTED, { event_category: 'onboarding' });
  }

  onboardingCompleted(destination) {
    this.pushToGA4('tutorial_complete');
    return this.track(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
      event_category: 'onboarding',
      destination
    });
  }

  onboardingSkipped() {
    return this.track(ANALYTICS_EVENTS.ONBOARDING_SKIPPED, { event_category: 'onboarding' });
  }

  getSessionStats() {
    return {
      sessionId: this.sessionId,
      eventCount: this.events.length,
      sessionDuration: Date.now() - this.startTime,
      events: this.events
    };
  }
}

const analytics = new Analytics();
export default analytics;
export { Analytics };
